import type { Mass } from './mass.ts';

/** Personal accessory rules — NOT official StrongLifts prescriptions (brief US-09/US-10). */

export interface CurlRule {
  sets: number;
  reps: number;
  /** Available dumbbell weights (per hand), any order. */
  dumbbells: readonly Mass[];
}

export interface CurlState {
  weight: Mass;
}

/** Double progression on dumbbells: all sets hit target reps → next dumbbell up; otherwise repeat. */
export function nextCurl(state: CurlState, rule: CurlRule, logged: readonly (number | null)[]): CurlState & { progressed: boolean } {
  const done = logged.length === rule.sets && logged.every((r) => r !== null && r >= rule.reps);
  if (!done) return { ...state, progressed: false };
  const heavier = [...rule.dumbbells].sort((a, b) => a - b).find((d) => d > state.weight);
  return heavier === undefined ? { ...state, progressed: false } : { weight: heavier, progressed: true };
}

export interface PushupRule {
  /** Reps added per set after a successful session. */
  repStep: number;
  /** When a set's target would exceed this, add a set and reset reps. */
  maxReps: number;
  resetReps: number;
  maxSets: number;
}

export interface PushupState {
  sets: number;
  reps: number;
}

export const DEFAULT_PUSHUP_RULE: PushupRule = { repStep: 1, maxReps: 15, resetReps: 10, maxSets: 6 };

/** All sets at target → +repStep; reaching maxReps → +1 set at resetReps. Incomplete → repeat. */
export function nextPushups(state: PushupState, rule: PushupRule, logged: readonly (number | null)[]): PushupState & { progressed: boolean } {
  const done = logged.length === state.sets && logged.every((r) => r !== null && r >= state.reps);
  if (!done) return { ...state, progressed: false };
  const reps = state.reps + rule.repStep;
  if (reps > rule.maxReps) {
    if (state.sets >= rule.maxSets) return { ...state, progressed: false };
    return { sets: state.sets + 1, reps: rule.resetReps, progressed: true };
  }
  return { sets: state.sets, reps, progressed: true };
}

export function totalReps(logged: readonly (number | null)[]): number {
  return logged.reduce<number>((s, r) => s + (r ?? 0), 0);
}
